/**
 * components/tableElements/ActionDropdown.jsx
 *
 * Non-trashed forms show:  Edit · Duplicate · Publish/Draft toggle · Move to Trash
 * Trashed forms show:      Restore · Delete Permanently (with confirm modal)
 *
 * Edit and Duplicate are hidden for trashed forms since the builder
 * shouldn't open a trashed form.
 */

import { useState, useRef, useEffect, createPortal } from "@wordpress/element";
import {
    MoreHorizontal,
    Pencil,
    Copy,
    ToggleLeft,
    Trash2,
    RotateCcw,
    AlertTriangle,
} from "lucide-react";
import { getEditUrl } from "../../context/CrudContext";
import ConfirmModal from "./ConfirmModal";

export default function ActionDropdown({
    form,
    onToggleStatus,
    onDuplicate,
    onTrash,
    onRestore,
    onPermanentDelete,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({
        top: 0,
        right: 0,
        left: undefined,
    });
    const [confirmPermDelete, setConfirmPermDelete] = useState(false);
    const [confirmDupe, setConfirmDupe] = useState(false);
    const buttonRef = useRef(null);

    const calcPosition = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const isRTL = document.documentElement.getAttribute("dir") === "rtl";
        setPosition({
            top: rect.bottom + 4,
            right: isRTL ? undefined : window.innerWidth - rect.right,
            left: isRTL ? rect.left : undefined,
        });
    };

    useEffect(() => {
        if (isOpen) requestAnimationFrame(calcPosition);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener("resize", calcPosition);
        window.addEventListener("scroll", calcPosition, true);
        return () => {
            window.removeEventListener("resize", calcPosition);
            window.removeEventListener("scroll", calcPosition, true);
        };
    }, [isOpen]);

    const close = () => setIsOpen(false);
    const portalTarget = document.getElementById("wpbean-pgs-settings-app");
    const isTrashed = form.status === "trash";
    const isPublished = form.status === "publish";

    const dropdown = isOpen && (
        <>
            <div className="fixed inset-0 z-10" onClick={close} />

            <div
                className="fixed w-52 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 py-1 z-20"
                style={{
                    top: `${position.top}px`,
                    ...(position.left !== undefined
                        ? { left: `${position.left}px` }
                        : { right: `${position.right}px` }),
                }}
            >
                {!isTrashed && (
                    <>
                        <a
                            href={getEditUrl(form.id)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 no-underline"
                        >
                            <Pencil className="w-4 h-4 me-3 flex-shrink-0" />
                            Edit
                        </a>

                        <button
                            onClick={() => {
                                close();
                                setConfirmDupe(true);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <Copy className="w-4 h-4 me-3 flex-shrink-0" />
                            Duplicate
                        </button>

                        <button
                            onClick={() => {
                                close();
                                onToggleStatus(form);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <ToggleLeft className="w-4 h-4 me-3 flex-shrink-0" />
                            {isPublished ? "Set to Draft" : "Publish"}
                        </button>

                        <hr className="my-1 border-gray-100" />

                        <button
                            onClick={() => {
                                close();
                                onTrash(form.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                            <Trash2 className="w-4 h-4 me-3 flex-shrink-0" />
                            Move to Trash
                        </button>
                    </>
                )}

                {isTrashed && (
                    <>
                        <button
                            onClick={() => {
                                close();
                                onRestore(form.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            <RotateCcw className="w-4 h-4 me-3 flex-shrink-0" />
                            Restore
                        </button>

                        <hr className="my-1 border-gray-100" />

                        <button
                            onClick={() => {
                                close();
                                setConfirmPermDelete(true);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                            <AlertTriangle className="w-4 h-4 me-3 flex-shrink-0" />
                            Delete Permanently
                        </button>
                    </>
                )}
            </div>
        </>
    );

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen((o) => !o)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                aria-label="Row actions"
            >
                <MoreHorizontal className="w-4 h-4" />
            </button>

            {isOpen && portalTarget && createPortal(dropdown, portalTarget)}

            {/* Permanent delete confirmation */}
            <ConfirmModal
                open={confirmPermDelete}
                title="Delete permanently?"
                description={`"${form.title}" will be deleted forever and cannot be recovered.`}
                confirmText="Delete Permanently"
                danger
                onConfirm={() => {
                    setConfirmPermDelete(false);
                    onPermanentDelete(form.id);
                }}
                onCancel={() => setConfirmPermDelete(false)}
            />

            {/* Duplicate confirmation */}
            <ConfirmModal
                open={confirmDupe}
                title="Duplicate form?"
                description={`A copy of "${form.title}" will be created as a draft.`}
                confirmText="Duplicate"
                onConfirm={() => {
                    setConfirmDupe(false);
                    onDuplicate(form);
                }}
                onCancel={() => setConfirmDupe(false)}
            />
        </div>
    );
}
